import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import {
  ChatCircleDots,
  PenNib,
  Eye,
  RocketLaunch,
} from "@phosphor-icons/react";
import { MaskLine, EASE, Counter } from "@/lib/anim";

const ACC = {
  teal: { text: "text-teal", ring: "border-teal", tile: "from-teal/30 to-teal/5 border-teal/40 text-teal", glow: "shadow-[0_0_30px_-6px_rgba(64,224,208,0.7)]", bar: "bg-teal" },
  cobalt: { text: "text-cobalt", ring: "border-cobalt", tile: "from-cobalt/30 to-cobalt/5 border-cobalt/40 text-cobalt", glow: "shadow-[0_0_30px_-6px_rgba(59,110,255,0.7)]", bar: "bg-cobalt" },
  violet: { text: "text-violet", ring: "border-violet", tile: "from-violet/30 to-violet/5 border-violet/40 text-violet", glow: "shadow-[0_0_30px_-6px_rgba(139,92,246,0.7)]", bar: "bg-violet" },
  magenta: { text: "text-magenta", ring: "border-magenta", tile: "from-magenta/30 to-magenta/5 border-magenta/40 text-magenta", glow: "shadow-[0_0_30px_-6px_rgba(236,72,153,0.7)]", bar: "bg-magenta" },
};

// ---- Mini illustrations per step ----
const VisionGraphic = () => (
  <div className="flex flex-col gap-1.5 items-start">
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: -10 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 + i * 0.15 }}
        className={`h-3 rounded-full ${i === 1 ? "bg-teal/60 w-16 self-end" : "bg-white/15 w-20"}`}
      />
    ))}
  </div>
);

const BlueprintGraphic = () => (
  <div className="relative h-16 w-16">
    <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="border border-cobalt/20" />
      ))}
    </div>
    <motion.div
      initial={{ pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.2 }}
      className="absolute left-1 top-1 h-3 w-3 rounded-sm bg-cobalt"
    />
    <motion.div
      animate={{ opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 2, repeat: Infinity }}
      className="absolute right-1 bottom-1 h-3 w-3 rounded-sm bg-cobalt/70"
    />
  </div>
);

const PreviewGraphic = () => (
  <div className="w-24 rounded-lg border border-violet/25 bg-black/40 overflow-hidden">
    <div className="flex gap-1 px-2 py-1.5 border-b border-white/10">
      <span className="h-1.5 w-1.5 rounded-full bg-violet/70" />
      <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
    </div>
    <div className="p-2 space-y-1.5">
      <div className="h-2 w-3/4 rounded bg-white/15" />
      <div className="h-2 w-1/2 rounded bg-white/10" />
      <motion.div
        initial={{ width: "10%" }}
        whileInView={{ width: "90%" }}
        viewport={{ once: false }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        className="h-1.5 rounded-full bg-gradient-to-r from-violet to-cobalt"
      />
    </div>
  </div>
);

const LaunchGraphic = () => (
  <div className="flex items-end gap-1.5 h-16">
    {[8, 12, 18].map((h, i) => (
      <motion.div
        key={i}
        initial={{ height: 4 }}
        whileInView={{ height: h * 3 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 + i * 0.15, type: "spring", stiffness: 120 }}
        className="w-3 rounded-t bg-gradient-to-t from-magenta/40 to-magenta"
      />
    ))}
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 1.8, repeat: Infinity }}
      className="ml-1 text-magenta"
    >
      <RocketLaunch size={22} weight="fill" />
    </motion.div>
  </div>
);

const STEPS = [
  { icon: ChatCircleDots, n: "01", accent: "teal", title: "Tell us the vision", body: "A free build consult. You talk, we listen — your business, your bottlenecks, the thing you wish existed.", Graphic: VisionGraphic },
  { icon: PenNib, n: "02", accent: "cobalt", title: "Get the blueprint", body: "We map the build: features, automations, timeline, and price. In writing. Before you commit a dollar.", Graphic: BlueprintGraphic },
  { icon: Eye, n: "03", accent: "violet", title: "We build, you watch", body: "Regular check-ins and live previews as your website, app, or automation takes shape. Feedback welcomed the whole way.", Graphic: PreviewGraphic },
  { icon: RocketLaunch, n: "04", accent: "magenta", title: "Launch and grow", body: "We ship it, train your team, and stay on for support — and layer in new automations as you scale.", Graphic: LaunchGraphic },
];

export const Process = () => {
  const lineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: lineRef,
    offset: ["start 65%", "end 55%"],
  });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.4 });

  return (
    <section
      id="process"
      className="relative py-28 md:py-40 bg-transparent overflow-hidden"
      data-testid="process-section"
    >
      <div className="absolute right-0 top-40 h-96 w-96 rounded-full bg-violet/10 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12 md:gap-20">
        {/* Sticky left */}
        <div className="md:sticky md:top-28 h-fit">
          <span className="text-xs uppercase tracking-[0.25em] text-teal/80">
            How it works
          </span>
          <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-white text-3xl sm:text-4xl md:text-5xl leading-[1.02]">
            <MaskLine>From your vision</MaskLine>
            <MaskLine delay={0.08}>to your build.</MaskLine>
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed max-w-md">
            Twelve years in the demand-generation trenches taught us one thing:
            everything we build is engineered for results — not for show.
          </p>

          {/* animated orbit graphic */}
          <div className="relative mt-10 h-40 w-40">
            {[1, 2, 3].map((r) => (
              <div
                key={r}
                className="absolute inset-0 rounded-full border border-white/10"
                style={{ inset: `${(r - 1) * 22}px` }}
              />
            ))}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0"
            >
              <span className="absolute left-1/2 top-0 -translate-x-1/2 h-3 w-3 rounded-full bg-teal shadow-glow-teal" />
            </motion.div>
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0"
              style={{ inset: "22px" }}
            >
              <span className="absolute left-1/2 top-0 -translate-x-1/2 h-2.5 w-2.5 rounded-full bg-cobalt shadow-[0_0_16px_4px_rgba(59,110,255,0.6)]" />
            </motion.div>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Counter
                to={12}
                className="font-heading text-5xl font-extrabold bg-gradient-to-r from-teal to-violet bg-clip-text text-transparent"
              />
              <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">years</span>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm">
            {[
              { to: 100, suffix: "%", label: "You own it" },
              { to: 3, suffix: "", label: "Build types" },
              { to: 0, suffix: "", label: "Templates used" },
            ].map((st) => (
              <div key={st.label} data-testid={`stat-${st.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Counter
                  to={st.to}
                  suffix={st.suffix}
                  className="font-heading text-3xl font-extrabold text-white"
                />
                <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-white/45">
                  {st.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div ref={lineRef} className="relative">
          {/* base line */}
          <div className="absolute left-[27px] top-4 bottom-4 w-[2px] bg-white/10" />
          {/* progress line */}
          <motion.div
            style={{ scaleY }}
            className="absolute left-[27px] top-4 bottom-4 w-[2px] origin-top bg-gradient-to-b from-teal via-cobalt to-magenta"
          />

          <div className="flex flex-col gap-8">
            {STEPS.map((s, i) => {
              const a = ACC[s.accent];
              const Icon = s.icon;
              const Graphic = s.Graphic;
              return (
                <div key={s.n} className="relative pl-24" data-testid={`process-step-${s.n}`}>
                  {/* node */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ type: "spring", stiffness: 240, damping: 14, delay: 0.1 }}
                    className={`absolute left-0 top-0 h-14 w-14 rounded-2xl bg-[#0A0A12] border-2 flex items-center justify-center ${a.ring} ${a.text} ${a.glow}`}
                  >
                    <Icon size={24} weight="duotone" />
                  </motion.div>

                  {/* card */}
                  <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
                    whileHover={{ y: -4 }}
                    className="group rounded-2xl border border-white/10 bg-[#0A0A12]/80 backdrop-blur-sm p-6 md:p-7 grid md:grid-cols-[1fr_auto] gap-6 items-center"
                  >
                    <div>
                      <div className="flex items-center gap-3">
                        <span className={`font-heading text-sm font-bold ${a.text}`}>{s.n}</span>
                        <h3 className="font-heading text-xl md:text-2xl font-bold text-white">{s.title}</h3>
                      </div>
                      <p className="mt-3 text-white/60 leading-relaxed">{s.body}</p>
                    </div>
                    <div className="hidden md:flex items-center justify-center min-w-[100px]">
                      <Graphic />
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
